/**
 * Loader helpers shared by the per-app `loaders.ts` files.
 */

import { getLogger } from '@logtape/logtape';
import { fetchWfsProperties, geoServer } from '#lib/config.js';
import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
import { operationalLayersStore } from '#lib/stores/operationalLayersStore.svelte.js';
import { isLayerInVersion, versionsStore } from '#lib/stores/versionsStore.svelte.js';
import { FetchStatus } from '#lib/types.js';

const log = getLogger(['platform', 'loaders']);

/**
 * System loader: builds from the key's operational features in `datasets`, each at its active
 * version. `build` returns null when the app's own data is missing, which the shell shows as
 * "Data not available".
 */
export async function loadSystemLayers<F, T>(
	apiKey: string,
	datasets: string[],
	build: (layers: F[]) => T | null
): Promise<T | null> {
	// Both stores load concurrently from the root layout; filtering before versions land drops every layer.
	await Promise.all([operationalLayersStore.load(apiKey), versionsStore.load(apiKey)]);
	for (const store of [operationalLayersStore, versionsStore]) {
		if (store.status === FetchStatus.FAILURE)
			throw new Error(store.error ?? 'Failed to load layers');
	}
	const layers = operationalLayersStore.layers.filter(
		(l) => datasets.includes(l.dataset) && isLayerInVersion(versionsStore.version(l.dataset), l)
	);
	return build(layers as F[]);
}

/**
 * Fetch `product`'s summary tables at `dataset`'s active version, keyed by module
 * (`type: summary` tables whose module is in `modules`) plus `location_label` from `getLocation`.
 * Throws when fewer than `expectedKeys` keys (`location_label` always counts as one) resolve so the shell can flip to FAILURE.
 */
export async function loadSummaryTables(
	apiKey: string,
	product: string,
	dataset: string,
	modules: string[],
	expectedKeys = 0,
	onMetadata?: (row: Record<string, string | number>) => void
): Promise<Record<string, unknown>> {
	const cred = apiKeyStore.credentials;
	if (!cred) throw new Error('No credentials — validate the api key first');

	const [tables, location] = await Promise.all([
		geoServer.getSummaryLayers(apiKey, cred.namespace),
		geoServer.getLocation(apiKey, cred.namespace),
		// The series filter below needs versions; the root layout's load may still be in flight.
		versionsStore.load(apiKey)
	]);

	const summary: Record<string, unknown> = { location_label: location?.label ?? '' };
	// getSummaryLayers returns every version's tables; without this the last one silently wins.
	const version = versionsStore.version(dataset);
	// No version means no data for the app; the system loader reports that as "Data not available".
	if (!version) return summary;
	for (const table of tables) {
		const { keywords, wfsTemplate } = table;
		if (keywords.product !== product || !isLayerInVersion(version, table)) continue;

		const rows = await fetchWfsProperties(wfsTemplate);
		if (rows.length === 0) continue;

		if (keywords.type === 'metadata') {
			onMetadata?.(rows[0]);
		} else if (keywords.type === 'summary' && modules.includes(keywords.module)) {
			summary[keywords.module] = rows;
		}
	}

	const loaded = Object.keys(summary).length;
	log.debug('Got Summary data keys: {keys}', { keys: JSON.stringify(Object.keys(summary)) });
	if (loaded < expectedKeys) {
		throw new Error(
			`Summary data incomplete: ${loaded} of ${expectedKeys} keys loaded. ` +
				`Dataset mismatch? (expected dataset "${dataset}")`
		);
	}
	return summary;
}
